/**
 * CSV export shaping for the DevOps boards.
 *
 * Same contract as `views/infrastructure/infrastructureExports.ts`: pure
 * row-to-record functions plus static {@link ColumnConfig} lists, so the
 * downloaded shape is unit-testable without a store or a socket. Stores choose
 * WHICH rows (the on-screen set: current filters + sort); this file is the only
 * thing that chooses which FIELDS.
 *
 * ── CREDENTIAL RULE ─────────────────────────────────────────────────────────
 * Every board here reads Azure DevOps through a `devops_connections` row whose
 * PAT is encrypted at rest and never serialised (the record carries `hasPat`
 * only). The proxied Azure rows — pipelines, releases, work items — carry no
 * credential of their own, and no shaper below touches a connection record.
 * Each record is built by EXPLICITLY NAMING its fields: no spread, no
 * `Object.keys` walk, no computed key. A field added to a type tomorrow cannot
 * reach a CSV without an edit here. `devopsExports.test.ts` pins it.
 *
 * ── WHAT "SCOPE" MEANS IN THESE FILES ───────────────────────────────────────
 * A board is always scoped to one connection + one project, and the rows carry
 * no column saying so. A CSV that omits it is a file nobody can interpret a
 * week later, so every shaper takes the resolved scope labels and stamps them
 * on each row.
 */
import type { ColumnConfig } from "shared/common/utils/export";
import {
  stripRefPrefix,
  type DevOpsPipeline,
  type DevOpsRelease,
  type DevOpsWorkItem,
} from "./devops.types";

/** A single flat CSV row. `exportToCsv` reads values by column `key`. */
export type ExportRecord = Record<string, string | number | null>;

/** The connection/project a board row was read through — stamped on every row. */
export interface DevOpsExportScope {
  connectionName: string;
  projectName: string;
  /** Display name of the client the connection is bound to; "" for the
   *  internal org (`devops_connections.clientId` null). */
  clientName?: string;
}

/** Timestamps go out as raw ISO-8601 (UTC) — see infrastructureExports. */
const iso = (value?: string | null): string | null => value ?? null;

/**
 * Make an Azure project/definition name safe for a download file name. Azure
 * names routinely contain spaces, slashes and dots, which produce a file the
 * browser silently renames (or, with a slash, a broken download).
 */
export function slugForFileName(value: string): string {
  return (
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 40) || "export"
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Work Items
// ─────────────────────────────────────────────────────────────────────────────

/** CSV shape of the Work Items table (see {@link toWorkItemExportRow}). */
export const WORK_ITEM_EXPORT_COLUMNS: ColumnConfig[] = [
  { key: "id", label: "ID" },
  { key: "workItemType", label: "Type" },
  { key: "title", label: "Title" },
  { key: "state", label: "State" },
  { key: "assignedTo", label: "Assigned To" },
  { key: "priority", label: "Priority", format: "number" },
  { key: "tags", label: "Tags" },
  { key: "areaPath", label: "Area Path" },
  { key: "iterationPath", label: "Iteration" },
  { key: "changedDate", label: "Changed (UTC)" },
  { key: "createdDate", label: "Created (UTC)" },
  { key: "url", label: "URL" },
  { key: "connection", label: "Connection" },
  { key: "project", label: "Project" },
  { key: "client", label: "Client" },
];

export function toWorkItemExportRow(
  item: DevOpsWorkItem,
  scope: DevOpsExportScope
): ExportRecord {
  return {
    id: item.id,
    workItemType: item.workItemType ?? "",
    title: item.title ?? "",
    state: item.state ?? "",
    assignedTo: item.assignedTo ?? "",
    priority: item.priority ?? null,
    tags: item.tags ?? "",
    areaPath: item.areaPath ?? "",
    iterationPath: item.iterationPath ?? "",
    changedDate: iso(item.changedDate),
    createdDate: iso(item.createdDate),
    url: item.url ?? "",
    connection: scope.connectionName,
    project: scope.projectName,
    client: scope.clientName ?? "",
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Pipelines board
// ─────────────────────────────────────────────────────────────────────────────

/** CSV shape of the Pipelines board (see {@link toPipelineExportRow}). */
export const PIPELINE_EXPORT_COLUMNS: ColumnConfig[] = [
  { key: "name", label: "Pipeline" },
  { key: "path", label: "Path" },
  { key: "latestStatus", label: "Latest Status" },
  { key: "latestState", label: "Latest State" },
  { key: "latestBuildNumber", label: "Build" },
  { key: "latestSourceBranch", label: "Branch" },
  { key: "latestFinishTime", label: "Finished (UTC)" },
  { key: "webUrl", label: "URL" },
  { key: "connection", label: "Connection" },
  { key: "project", label: "Project" },
];

export function toPipelineExportRow(
  pipeline: DevOpsPipeline,
  scope: DevOpsExportScope
): ExportRecord {
  return {
    name: pipeline.name ?? "",
    path: pipeline.path ?? "",
    latestStatus: pipeline.latestStatus ?? "",
    latestState: pipeline.latestState ?? "",
    latestBuildNumber: pipeline.latestBuildNumber ?? "",
    // The grid strips the `refs/heads/` prefix; the CSV matches what was read.
    latestSourceBranch: stripRefPrefix(pipeline.latestSourceBranch),
    latestFinishTime: iso(pipeline.latestFinishTime),
    webUrl: pipeline.webUrl ?? "",
    connection: scope.connectionName,
    project: scope.projectName,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Releases board
// ─────────────────────────────────────────────────────────────────────────────

/** CSV shape of the Releases board (see {@link toReleaseExportRow}). */
export const RELEASE_EXPORT_COLUMNS: ColumnConfig[] = [
  { key: "name", label: "Release" },
  { key: "definitionName", label: "Definition" },
  { key: "status", label: "Status" },
  { key: "createdOn", label: "Created (UTC)" },
  { key: "modifiedOn", label: "Modified (UTC)" },
  { key: "description", label: "Description" },
  { key: "webUrl", label: "URL" },
  { key: "connection", label: "Connection" },
  { key: "project", label: "Project" },
];

export function toReleaseExportRow(
  release: DevOpsRelease,
  scope: DevOpsExportScope
): ExportRecord {
  return {
    name: release.name ?? "",
    // Mirror the grid's fallback so a definition with no name is still
    // identifiable in the spreadsheet.
    definitionName:
      release.definitionName ??
      (release.definitionId ? `Definition ${release.definitionId}` : ""),
    status: release.status ?? "",
    createdOn: iso(release.createdOn),
    modifiedOn: iso(release.modifiedOn),
    description: release.description ?? "",
    webUrl: release.webUrl ?? "",
    connection: scope.connectionName,
    project: scope.projectName,
  };
}
